<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { MessageSquarePlus, ArrowLeft } from 'lucide-vue-next'
import ChatSessionsList from '@/components/chat/ChatSessionsList.vue'
import ChatConversation from '@/components/chat/ChatConversation.vue'
import { useChatWebSocket } from '@/composables/useChatWebSocket'
import { formatTime } from '@/utils/datetime'

const route = useRoute()
const router = useRouter()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const sessionCode = computed(() => route.params.session_code)

// State
const isGenerating = ref(false)

// Initialize WebSocket
const chatWs = useChatWebSocket()
let previousSessionCode = null

// Get data from Vuex store
const chatSessions = computed(() => {
  return store.getters['chat/getOrgSessions'](orgId.value)
})

const currentMessages = computed(() => {
  if (!sessionCode.value) return []
  return store.getters['chat/getSessionMessages'](sessionCode.value)
})

const currentSession = computed(() => {
  if (!sessionCode.value) return null
  return store.getters['chat/getSession'](orgId.value, sessionCode.value)
})

// View state - based on route
const showChatView = computed(() => !!sessionCode.value)

// Select a session - navigate to it
const selectSession = (session) => {
  console.log('Selecting session:', session)
  store.dispatch('chat/setActiveSession', session.session_code)
  router.push({
    name: 'ChatSession',
    params: {
      org_id: orgId.value,
      session_code: session.session_code
    }
  })
}

// Back to list - navigate to OrgReports
const backToList = () => {
  router.push({ name: 'OrgReports', params: { org_id: orgId.value } })
}

// Send message
const sendMessage = async (content) => {
  if (isGenerating.value || !sessionCode.value) return

  console.log('Sending message:', content)

  try {
    // Add message to local state
    await store.dispatch('chat/sendMessage', {
      orgId: orgId.value,
      sessionCode: sessionCode.value,
      content: content,
      role: 'user'
    })
  } catch (error) {
    console.error('Error sending message:', error)
    toast({
      title: 'Error',
      description: 'Failed to send message',
      variant: 'destructive'
    })
  }
}

// Create new chat session directly (no dialog)
const createNewChat = async () => {
  const userTz = store.state.user.tz || 'UTC'
  const defaultName = formatTime(new Date(), userTz, 'MMMM DD, YYYY')

  console.log('Creating new chat with auto-generated name:', defaultName)

  try {
    // Create session with auto-generated date name
    const newSession = await store.dispatch('chat/createSession', {
      orgId: orgId.value,
      name: defaultName
      // No llm_config - server uses defaults
    })

    // Navigate to the new session
    selectSession(newSession)
  } catch (error) {
    console.error('Error creating chat:', error)
    toast({
      title: 'Error',
      description: 'Failed to create chat session',
      variant: 'destructive'
    })
  }
}

// Delete session
const deleteSession = async (session) => {
  console.log('Deleting session:', session)

  try {
    await store.dispatch('chat/deleteSession', {
      orgId: orgId.value,
      sessionCode: session.session_code
    })

    toast({
      title: 'Deleted',
      description: 'Chat session deleted',
      variant: 'default'
    })

    // If we deleted the current session, navigate back to list
    if (sessionCode.value === session.session_code) {
      backToList()
    }
  } catch (error) {
    console.error('Error deleting session:', error)
    toast({
      title: 'Error',
      description: 'Failed to delete session',
      variant: 'destructive'
    })
  }
}

// Update session name
const updateSession = async ({ session_code, name }) => {
  console.log('Updating session:', session_code, 'with name:', name)

  try {
    await store.dispatch('chat/updateSession', {
      orgId: orgId.value,
      sessionUuid: session_code,
      updates: { name }
    })

    toast({
      title: 'Updated',
      description: 'Chat name updated',
      variant: 'default'
    })
  } catch (error) {
    console.error('Error updating session:', error)
    toast({
      title: 'Error',
      description: 'Failed to update chat name',
      variant: 'destructive'
    })
  }
}

// WebSocket event handlers
const handleMessageCreated = (messageData) => {
  console.log('[AIChat] WebSocket message created:', messageData)

  // Transform API response to match store format
  const message = {
    message_code: messageData.message_uuid,
    message_uuid: messageData.message_uuid,
    session_code: sessionCode.value,
    role: messageData.role,
    content: messageData.content,
    created_at: messageData.created_at,
    updated_at: messageData.updated_at,
    message_metadata: messageData.message_metadata || null
  }

  // Add to Vuex store (only if not already there to avoid duplicates)
  store.commit('chat/ADD_MESSAGE', {
    sessionCode: sessionCode.value,
    message
  })
}

// Streaming state
const streamingMessage = ref(null)

const handleStreamStart = () => {
  console.log('[AIChat] Stream started')
  isGenerating.value = true

  // Create a temporary streaming message
  streamingMessage.value = {
    message_code: 'streaming',
    message_uuid: 'streaming',
    session_code: sessionCode.value,
    role: 'assistant',
    content: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }

  // Add streaming message to store
  store.commit('chat/ADD_MESSAGE', {
    sessionCode: sessionCode.value,
    message: streamingMessage.value
  })
}

const handleMessageChunk = (data) => {
  console.log('[AIChat] Message chunk:', data.chunk)

  if (streamingMessage.value) {
    // Append chunk to streaming message
    streamingMessage.value.content += data.chunk

    // Update the message in the store
    store.commit('chat/UPDATE_MESSAGE', {
      sessionCode: sessionCode.value,
      messageCode: 'streaming',
      updates: {
        content: streamingMessage.value.content
      }
    })
  }
}

const handleStreamComplete = (data) => {
  console.log('[AIChat] Stream complete:', data)
  isGenerating.value = false

  // Handle both data.message and data.message_data for backwards compatibility
  const messageData = data.message || data.message_data

  if (streamingMessage.value && messageData) {
    // Replace streaming message with final message
    store.commit('chat/DELETE_MESSAGE', {
      sessionCode: sessionCode.value,
      messageCode: 'streaming'
    })

    const finalMessage = {
      message_code: messageData.message_uuid,
      message_uuid: messageData.message_uuid,
      session_code: sessionCode.value,
      role: messageData.role,
      content: messageData.content,
      created_at: messageData.created_at,
      updated_at: messageData.updated_at,
      message_metadata: messageData.message_metadata || null
    }

    store.commit('chat/ADD_MESSAGE', {
      sessionCode: sessionCode.value,
      message: finalMessage
    })
  }

  streamingMessage.value = null

  // Navigate to report if one was created/updated
  if (data.report) {
    const { report_uuid } = data.report
    router.push({
      name: 'ChatSession',
      params: route.params,
      query: { ...route.query, report: report_uuid }
    })
  }
}

const handleWebSocketError = (error) => {
  console.error('[AIChat] WebSocket error:', error)
  isGenerating.value = false

  // Remove streaming message if it exists
  if (streamingMessage.value) {
    store.commit('chat/DELETE_MESSAGE', {
      sessionCode: sessionCode.value,
      messageCode: 'streaming'
    })
    streamingMessage.value = null
  }

  toast({
    title: 'Error',
    description: error.message || 'An error occurred',
    variant: 'destructive'
  })
}

// Register WebSocket event handlers
const unsubscribeMessageCreated = chatWs.onMessageCreated(handleMessageCreated)
const unsubscribeStreamStart = chatWs.onStreamStart(handleStreamStart)
const unsubscribeMessageChunk = chatWs.onMessageChunk(handleMessageChunk)
const unsubscribeStreamComplete = chatWs.onStreamComplete(handleStreamComplete)
const unsubscribeError = chatWs.onError(handleWebSocketError)

// Watch for session code changes
watch(sessionCode, async (newCode, oldCode) => {
  // Unsubscribe from previous session
  if (oldCode && previousSessionCode) {
    console.log('[AIChat] Unsubscribing from session:', oldCode)
    chatWs.unsubscribeFromSession(oldCode)
    previousSessionCode = null
  }

  if (newCode) {
    console.log('Session changed to:', newCode)
    store.dispatch('chat/setActiveSession', newCode)

    // Fetch session details (including messages)
    try {
      await store.dispatch('chat/fetchSession', { orgId: orgId.value, sessionUuid: newCode })

      // If WebSocket is already authenticated, subscribe immediately
      if (chatWs.isAuthenticated.value) {
        console.log('[AIChat] Subscribing to new session:', newCode)
        chatWs.subscribeToSession(newCode)
        previousSessionCode = newCode
      }
      // Otherwise, the isAuthenticated watcher will handle it
    } catch (error) {
      console.error('Error fetching session details:', error)
      toast({
        title: 'Error',
        description: 'Failed to load session messages',
        variant: 'destructive'
      })
    }
  }
}, { immediate: true })

// Watch for WebSocket authentication status
// When authenticated, subscribe to the current session if we have one
watch(() => chatWs.isAuthenticated.value, (authenticated) => {
  if (authenticated && sessionCode.value && sessionCode.value !== previousSessionCode) {
    console.log('[AIChat] WebSocket authenticated, subscribing to session:', sessionCode.value)
    chatWs.subscribeToSession(sessionCode.value)
    previousSessionCode = sessionCode.value
  }
})

// Fetch sessions and connect WebSocket on mount
onMounted(async () => {
  console.log('AIChat mounted, fetching sessions for org:', orgId.value)

  // Connect to WebSocket (subscription will happen automatically via the watch)
  try {
    await chatWs.connect()
    console.log('[AIChat] WebSocket connected successfully')
    // Note: Subscription will be handled by the isAuthenticated watcher
  } catch (error) {
    console.error('[AIChat] Failed to connect WebSocket:', error)
    toast({
      title: 'Connection Error',
      description: 'Failed to connect to real-time chat',
      variant: 'destructive'
    })
  }

  // Fetch chat sessions
  try {
    await store.dispatch('chat/fetchSessions', { orgId: orgId.value })
  } catch (error) {
    console.error('Error fetching chat sessions:', error)
    toast({
      title: 'Error',
      description: 'Failed to load chat sessions',
      variant: 'destructive'
    })
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  console.log('[AIChat] Component unmounting, cleaning up WebSocket')

  // Unsubscribe from current session
  if (previousSessionCode) {
    chatWs.unsubscribeFromSession(previousSessionCode)
  }

  // Cleanup event handlers
  unsubscribeMessageCreated()
  unsubscribeError()

  // WebSocket will auto-disconnect via composable's onUnmounted
})
</script>

<template>
  <div class="flex flex-col overflow-hidden min-h-full">
    <div class="px-2 pb-2 border-b border-gray-200 flex-shrink-0">
      <div class="flex items-center justify-between">
        <div v-if="!showChatView">
          <h2 class="ml-3 text-sm font-semibold">Chats</h2>
        </div>
        <Button
          v-else
          @click="backToList"
          variant="ghost"
          size="sm"
          class="gap-2"
        >
          <ArrowLeft class="h-4 w-4" />
          All Chats
        </Button>

        <Button @click="createNewChat" size="sm" variant="ghost" class="gap-1">
          <MessageSquarePlus class="h-4 w-4" />
          New
        </Button>
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-hidden">
      <ChatSessionsList
        v-if="!showChatView"
        :sessions="chatSessions"
        :active-session="null"
        @select="selectSession"
        @delete="deleteSession"
        @update="updateSession"
      />

      <ChatConversation
        v-else
        :messages="currentMessages"
        :is-generating="isGenerating"
        :session="currentSession"
        @send-message="sendMessage"
      />
    </div>
  </div>
</template>
